/**
 * 「这一组凭什么像一套」——右回显列那行一致性承诺的判定(B1-4,2026-07-31)。
 *
 * ═══ 为什么必须判准 ═══════════════════════════════════════════════════════
 * 版式锚那句话写在 prompt 里,用户看不见。界面上不说,他就不知道我们锁了什么;
 * 说错了更糟 —— **锚在两种情况下根本不生效**,这时候还写着「全组同版式」就是撒谎:
 *
 *   ① 用户自己挂了原图(i2i):ai 侧 `anchor_mode` 显式排除 `goes_i2i_initial` ——
 *      那张原图就是锚,不再拿第 1 张当锚。每张各自参照原图,是另一种一致(也够用)。
 *   ② 用户在 ⚙ 里关掉了「保持主体一致」:六张各自发挥,快,但不像一套。
 *
 * ★输入必须是**提交时真正会发出去的那几个值**(activeGroupPoints / activeRatioSet /
 *   keepSubjectConsistent),不是界面上另算一遍 —— 另算一份必漂,而漂移的后果是
 *   用户按这行字做决定、实际发出去的是另一回事。
 */
import type { OutputMode } from "./outputMode";

export type ConsistencyKind =
  /** 组图:第 1 张定版式,其余照着排(GROUP_ANCHOR_PREFIX) */
  | "layout"
  /** 套图:第 1 张定主体,各张按自己画幅重排(ANCHOR_I2I_PREFIX) */
  | "subject"
  /** 用户挂了原图:每张都参照那张,不需要第 1 张当锚 */
  | "ownRef"
  /** 关掉了:各自发挥 */
  | "off";

export function consistencyKind(input: {
  mode: OutputMode;
  isI2I: boolean;
  /** 提交时真正会发的那个值(单张时后端忽略,这里也不显示) */
  keepSubjectConsistent: boolean;
  /** 真实出图张数(effectiveCount) */
  count: number;
}): ConsistencyKind | null {
  // 单张没有「一致」可言 —— 与 ai 侧 anchor_mode 的 `len(variants) > 1` 同源
  if (input.count <= 1) return null;
  if (input.mode === "MULTI") return null; // 多张同款本来就是"同一张的 N 版",不承诺什么
  if (input.isI2I) return "ownRef";
  if (!input.keepSubjectConsistent) return "off";
  return input.mode === "GROUP" ? "layout" : "subject";
}

/** 那一行的标题与展开后的说明 —— 措辞要如实,锁不住的别写。 */
export const CONSISTENCY_NOTE: Record<ConsistencyKind, { label: string; detail: string }> = {
  layout: {
    label: "全组同版式 · 主体锚定",
    detail:
      "先出第 1 张,其余几张照着它排:版式结构、配色、光线、镜头距离、主标题位置与留白比例保持一致,换的是每张的场景内容与大字。",
  },
  subject: {
    label: "主体一致 · 各按画幅重排",
    detail: "先出第 1 张,其余几张以它为准保留产品 / 人物的外观材质;构图按各自的画幅重新安排。",
  },
  ownRef: {
    label: "每张都参照你上传的原图",
    detail: "你挂了原图,每张都以它为参考出图 —— 不再拿第 1 张当锚,所以也不必逐张等着(比锚定快)。",
  },
  off: {
    label: "每张各自发挥",
    detail:
      "没有开「保持主体一致」:出得快,但主体与版式可能各不相同,拼成一组会看得出来。要像一套就在 ⚙ 里打开它。",
  },
};
